import React, { useEffect, useMemo } from 'react';
import { Col, Row } from 'react-styled-flexboxgrid';
import { ApiWorkers } from '../../../../../../api/workers';
import Modal from '../../../../../../components/Modal';
import FormJobDeleteCandidate from '../../../../../../forms/FormJobDeleteCandidate';
import { useApi } from '../../../../../../hooks/useApi';
import { useManage } from '../../../../../../hooks/useManage';
import { OPERATIONS, ROLES } from '../../../../../../utils/constans';
import { findByID, parseCandidates } from '../../../../../../utils/helpers';
import Button from '../../../../../../components/Button';
import ButtonGroup from '../../../../../../components/ButtonGroup';
import Icon from '../../../../../../components/Icon';
import RowSubComponent from '../RowSubComponent';
import { makeStyles } from '../../../../../../utils/styles';
import Divider from '../../../../../../components/Divider';
import Box from '../../../../../../components/Box';
import Typography from '../../../../../../components/Typography';
import User from '../../../../../../components/User';
import LoaderImage from '../../../../../../components/LoaderImage';
import { generateTablePdf, parseDataToPDFBody } from '../../../../../../utils/pdf';
import { format, fromUnixTime, isBefore } from 'date-fns';
import { ApiApplications } from '../../../../../../api/applications';
import Loader from '../../../../../../components/Loader';
import { toast } from 'react-toastify';
import { ApiChat } from '../../../../../../api/chat';
import FormJobMessage from '../../../../../../forms/FormJobMessage';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { paths } from '../../../../../../routes';

const useStyles = makeStyles(css => ({
    boxAsync: css`
        position: relative;
    `,
    box: css`
        padding: 0 35px;
    `,
    boxUsers: css`
        padding: 35px 0;
    `,
    usersHeader: css`
        margin-bottom: 35px;
    `,
    boxUser: css`
        padding: 0 10px;
    `,
    dividerBottom: css`
        margin-bottom: 15px;
    `
}));

const RowSubComponentActiveAsync = ({ row, rowProps, refetchJobsActive, onClickEdit, onClickDuplicate, onClickDelete }) => {
    const { t } = useTranslation()
    const styles = useStyles();
    const [{ operation, id: activeID }, setState] = useManage();

    const [{ data: candidates, loading: candidatesLoading }, getActiveCandidatesByJob] = useApi(ApiWorkers.getActiveCandidatesByJob, { manual: true });
    const [{ loading: cancelApplicationLoading }, cancelApplication] = useApi(ApiApplications.cancelApplication, { manual: true });
    const [{ loading: createChatByUsersLoading }, createChatByUsers] = useApi(ApiChat.createChatByUsers, { manual: true });

    const role = useSelector(state => state.auth.user?.user?.role?.type);

    // Memoize data
    const candidatesParsed = useMemo(
        () => candidates && parseCandidates(candidates),
        [candidates]
    );

    const candidate = useMemo(
        () => candidatesParsed && findByID(candidatesParsed, activeID),
        [candidatesParsed, activeID]
    );

    const job = useMemo(
        () => ({
            ...row.original
        }),
        [row]
    );

    const jobTimeStart = useMemo(() => job?.time_start && fromUnixTime(job?.time_start), [job]);

    useEffect(() => {
        getActiveCandidatesByJob(job?.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClickDeleteCandidate = id => setState(OPERATIONS.DELETE, id);

    const handleClickCancelModal = () => setState(OPERATIONS.READ);

    const handleClickSendMessage = id => () => setState(OPERATIONS.MESSAGE, id);

    const handleClickDelete = id => () => onClickDelete(id);
    const handleClickEdit = id => () => onClickEdit(id);
    const handleClickDuplicate = id => () => onClickDuplicate(id);
    const handleClickPrintList = () => {
        const parsedData = parseDataToPDFBody(candidatesParsed, (candidate) => [candidate.username, '', '', '', '', '']);

        generateTablePdf({
            title: job?.place_id?.name,
            subtitle: t("Lista obecności: {{date}}", { date: format(jobTimeStart, 'dd.MM.yyyy') }) + `, ${job?.time_range}`,
            rows: [{
                title: t('Nazwa zlecenia'),
                text: job?.title,
            }, {
                title: t('Stanowisko'),
                text: job?.profession,
            }, {
                title: t('Manager'),
                text: job?.supplier,
            }],
            tableTitle: t('Lista pracowników'),
            table: {
                head: [
                    [
                        t('Pracownik'),
                        t('Rozpoczęcie pracy'),
                        t('Zakończenie pracy'),
                        t('Czas pracy'),
                        t('Podpis pracownika'),
                        t('Podpis przełożonego')
                    ]
                ],
                body: parsedData,
                signature: t('Zatwierdził')
            },
            filename: t("Lista obecności: {{date}}", { date: format(jobTimeStart, 'dd.MM.yyyy') }) + `, ${job?.time_range}`
        });
    }

    const handleSubmitDeleteCandidate = async data => {
        const res = await cancelApplication(candidate?.application_id, data);
        if (res?.data) {
            toast.success(t('Kandydat został usunięty'));
            setState(OPERATIONS.READ, null);
            await getActiveCandidatesByJob(job?.id);
            await refetchJobsActive();
        }
    };

    const handleSubmitMessage = async ({ message }) => {
        const candidateIDs = candidatesParsed.map(candidate => candidate?.user_id?.toString());
        const res = await createChatByUsers(candidateIDs, message);
        if (res?.data) {
            toast.success(t('Wiadomość została wysłana'));
            setState(OPERATIONS.READ, null);
        }
    };

    if (candidatesLoading) {
        return (
            <Box
                customStyle={styles.box}
            >
                <Row center="xs">
                    <Col>
                        <LoaderImage />
                    </Col>
                </Row>
            </Box>
        );
    }

    return (
        <Box
            customStyle={styles.boxAsync}
        >
            <RowSubComponent
                row={row}
                rowProps={rowProps}
                footerContent={(
                    <Row between="xs">
                        <Col>
                            <ButtonGroup
                                gutters={10}
                            >
                                {
                                    (jobTimeStart && isBefore(new Date(), jobTimeStart)) &&
                                    <Button
                                        variant="text"
                                        startIcon={<Icon name="edit" />}
                                        onClick={handleClickEdit(row?.original?.id)}
                                    >
                                       {t(" EDYTUJ ZLECENIE")}
                                    </Button>
                                }
                                {
                                    ((jobTimeStart && isBefore(new Date(), jobTimeStart)) || (candidatesParsed && candidatesParsed.length === 0)) &&
                                    <Button
                                        variant="text"
                                        startIcon={<Icon name="delete" />}
                                        onClick={handleClickDelete(row?.original?.id)}
                                    >
                                        {t("USUŃ ZLECENIE")}
                                    </Button>
                                }
                            </ButtonGroup>
                        </Col>
                        <Col>
                            <ButtonGroup
                                gutters={10}
                            >
                                {
                                    role === ROLES.SUPERADMIN &&
                                    <Button
                                        variant="text"
                                        startIcon={<Icon name="chat" />}
                                        onClick={handleClickSendMessage(row?.original?.id)}
                                    >
                                        {t("WYŚLIJ WIADOMOŚĆ")}
                                    </Button>
                                }
                                <Button
                                    variant="text"
                                    startIcon={<Icon name="duplicate" />}
                                    onClick={handleClickDuplicate(row?.original?.id)}
                                >
                                    {t("DUPLIKUJ ZLECENIE")}
                                </Button>
                                <Button
                                    variant="text"
                                    startIcon={<Icon name="print" />}
                                    onClick={handleClickPrintList}
                                >
                                    {t("DRUKUJ LISTĘ OBECNOŚĆI")}
                                </Button>
                            </ButtonGroup>
                        </Col>
                    </Row>
                )}
            >
                <Box
                    customStyle={styles.boxUsers}
                >
                    <Typography
                        as="h2"
                        customStyle={styles.usersHeader}
                    >
                        {t("Kandydaci do zlecenia")}
                    </Typography>
                    {
                        candidatesParsed && candidatesParsed.length > 0 ?
                            <Row>
                                {candidatesParsed.map((candidate, index) => (
                                    <Col key={`candidate-${candidate?.id}-index-${index}`}>
                                        {role === ROLES.SUPERADMIN ?
                                        <Link
                                            to={`${paths.workers}/${candidate?.id}`}
                                            style={{display: 'block'}}
                                        >
                                            <Box customStyle={styles.boxUser}>
                                                <User
                                                    user={candidate}
                                                    interactive
                                                    hideDelete={role !== ROLES.SUPERADMIN}
                                                    onClickDelete={handleClickDeleteCandidate}
                                                />
                                            </Box>
                                        </Link> :
                                            <Box customStyle={styles.boxUser}>
                                                <User
                                                    user={candidate}
                                                    interactive
                                                    hideDelete={role !== ROLES.SUPERADMIN}
                                                    onClickDelete={handleClickDeleteCandidate}
                                                />
                                            </Box>
                                        }              
                                    </Col>
                                ))}
                            </Row>
                            :
                            <Typography>
                                {t("Brak kandydatów")}
                            </Typography>
                    }
                </Box>
                <Divider customStyle={styles.dividerBottom} />
            </RowSubComponent>
            <Modal
                open={operation === OPERATIONS.DELETE}
            >
                <FormJobDeleteCandidate
                    loading={cancelApplicationLoading}
                    job={job}
                    candidate={candidate}
                    onSubmit={handleSubmitDeleteCandidate}
                    onClickCancel={handleClickCancelModal}
                />
            </Modal>
            <Modal
                open={operation === OPERATIONS.MESSAGE}
            >
                <FormJobMessage
                    loading={createChatByUsersLoading}
                    job={job}
                    onSubmit={handleSubmitMessage}
                    onClickCancel={handleClickCancelModal}
                />
            </Modal>
            <Loader show={cancelApplicationLoading} />
        </Box>
    );
};

export default RowSubComponentActiveAsync;
